import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import {
  ConfigPropertyOverrideService,
  POS_LOCAL_KEY_SHOW_DISPO,
  POS_LOCAL_KEY_SHOW_LAGERSTAND,
  POS_LOCAL_MODULE,
  WorkstationConfigService,
} from '@softapps/wws/pos-core';
import {
  backNavigation,
  handleRequestError,
  menuItems,
  ModuleConfig,
  RemoteConfigStore,
  routeData,
  SOFTLINE_FEATURE_REMOTE_CONFIG,
  title,
} from '@softline/application';
import {
  JwtAuthenticationStore,
  SOFTLINE_FEATURE_JWT_AUTHENTICATION,
} from '@softline/auth';
import { Store } from '@softline/core';
import { FieldOkComponent } from '@softline/dynamic';
import {
  FormatPipe,
  IconComponent,
  MessageBarService,
  SwitchComponent,
  ToggleSwitchComponent,
  translator,
  Validators,
} from '@softline/ui-core';
import { map, startWith } from 'rxjs';
import { SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY } from '@softapps/wws/pos-core';
import { SOFTLINE_CONFIG_WWS_CASH_REGISTER_PARAMS } from '@softapps/wws/pos-core';
import { DiscountConfigService } from '../../../../services/discount-config.service';
import { Configuration } from '../../types/config';
import { Kasse } from '../../types/kasse';
import { PaymentMethod } from '../../types/payment-method';

@Component({
  selector: 'soft-workstation',
  imports: [
    PageComponent,
    PageHeaderComponent,
    ReactiveFormsModule,
    ToggleSwitchComponent,
    SwitchComponent,
    FieldOkComponent,
    FormatPipe,
    IconComponent,
  ],
  templateUrl: './workstation.component.html',
  styleUrl: './workstation.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class WorkstationComponent implements AfterViewInit {
  readonly workstationService = inject(WorkstationConfigService);
  readonly discountConfigService = inject(DiscountConfigService);
  readonly cdRef = inject(ChangeDetectorRef);
  readonly #router = inject(Router);
  readonly #store = inject(Store);

  readonly messageBarService = inject(MessageBarService);
  readonly #injector = inject(Injector);

  readonly t = translator();

  readonly headerTitle = this.t('#CASH_REGISTER_CONFIG.WORKSTATION.HEADER_TITLE')();
  readonly headerSubtitle = this.t('#CASH_REGISTER_CONFIG.WORKSTATION.HEADER_SUBTITLE')();

  readonly overrides = inject(ConfigPropertyOverrideService);
  readonly #remoteConfig = this.#store.signal(
    SOFTLINE_FEATURE_REMOTE_CONFIG,
    RemoteConfigStore.getters.data
  );
  readonly moduleProperties = computed(() =>
    (
      this.#remoteConfig()?.modules?.find((m) => m.module === 'pos')?.properties ?? []
    ).map((p) => ({
      key: p.key,
      value: p.value,
      isBoolean: this.overrides.isBoolean(p.value),
      checked: this.overrides.resolve('pos', p.key, p.value),
    }))
  );

  togglePropertyOverride(key: string, checked: boolean): void {
    this.overrides.set('pos', key, checked);
  }

  toggleShowLagerstand(checked: boolean): void {
    this.overrides.set(POS_LOCAL_MODULE, POS_LOCAL_KEY_SHOW_LAGERSTAND, checked);
  }
  toggleShowDispo(checked: boolean): void {
    this.overrides.set(POS_LOCAL_MODULE, POS_LOCAL_KEY_SHOW_DISPO, checked);
  }

  /* TODO: offer every PAYMENT_METHOD_TYPE in the form — draft below, the reason it
     was disabled is not recorded.
  readonly paymentMethods = computed(() => {
    const payments = Object.keys(PAYMENT_METHOD_TYPE) as PaymentMethodType[];
    return payments ? payments : null;
  });*/

  readonly form = new FormGroup({
    name: new FormControl<Kasse | null>(null, {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    paymentMethod: new FormControl<PaymentMethod | null>(null, {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    paymentMethodMinus: new FormControl<PaymentMethod | null>(null, {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    autologin: new FormControl<boolean>(false, {
      nonNullable: true,
    }),
  });

  readonly isFormValid = toSignal(
    this.form.statusChanges.pipe(
      map((o) => o === 'VALID'),
      startWith(this.form.valid)
    ),
    { initialValue: false }
  );

  readonly hasConfigured = signal(false);

  readonly moduleParms = inject(SOFTLINE_CONFIG_WWS_CASH_REGISTER_PARAMS);

  readonly #routeData = routeData<{ config?: { modules?: ModuleConfig[] } }>();

  readonly discountsConfig = this.discountConfigService.getDiscountTemplateValues(
    this.#routeData
  );

  constructor() {
    title(() => {
      return this.hasConfigured()
        ? this.t('#CASH_REGISTER_CONFIG.WORKSTATION.TITLE_EDIT')()
        : this.t('#CASH_REGISTER_CONFIG.WORKSTATION.TITLE')();
    });

    backNavigation();
    void this.#store.dispatch(
      SOFTLINE_FEATURE_REMOTE_CONFIG,
      RemoteConfigStore.actions.loadOnce
    );
    menuItems(() => {
      return [
        {
          name: 'pos/config/workstation/submit',
          title: this.t('#CASH_REGISTER_CONFIG.WORKSTATION.SUBMIT')(),
          outlet: 'responsive',
          class: 'soft-button primary m-2',
          type: 'execute',
          icon: 'fa-regular fa-save',
          canExecute: this.isFormValid,
          execute: () => this.onSubmit(),
        },
        {
          name: 'pos/config/workstation',
          title: 'Kassenkonfiguration',
          icon: 'fa-regular fa-gear',
          type: 'route',
          outlet: 'nav',
          routerLink: ['/pos-config'],
        },
      ];
    });
  }

  async ngAfterViewInit(): Promise<void> {
    const config = await this.workstationService.load(
      SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY
    );

    this.hasConfigured.set(!!config);

    this.form.patchValue(config ?? ({} as any));
    this.form.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  async saveConfiguration() {
    const form = this.form.getRawValue();

    if (!form.name || !form.paymentMethod || !form.paymentMethodMinus) return;

    const config: Configuration = {
      name: form.name,
      paymentMethod: form.paymentMethod,
      paymentMethodMinus: form.paymentMethodMinus,
      autologin: form.autologin ?? false,
    };

    await this.workstationService.save(
      SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY,
      config
    );

    await this.discountConfigService.save();
  }

  async onSubmit() {
    try {
      await this.saveConfiguration();
      await this.#store.dispatch(
        SOFTLINE_FEATURE_JWT_AUTHENTICATION,
        JwtAuthenticationStore.actions.change,
        {
          workstation: this.form.value.name?.kassennummer,
          // configurable here, unlike the hardcoded firmaid sites in the
          // pos-display and self-checkout configs — this is the pattern to copy
          firmaid: this.moduleParms?.companyId ?? 1,
        }
      );
      this.messageBarService.success('Die Konfiguration wurde erfolgreich gespeichert.');

      await this.#router.navigate(['/']);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async addDiscountValue(type: 'item' | 'document'): Promise<void> {
    return this.discountConfigService.addLocalDiscount(type);
  }
}
